import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet } from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Toolbar } from "@/components/ui/toolbar";
import { FormSection, Field } from "@/components/ui/form-section";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";
import { useContexts, useCreateContext, useUpdateContext, useDeleteContext } from "@/hooks/use-contexts";
import type { DialplanContext } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const columns: Column<DialplanContext>[] = [
  { key: "name", header: "Name" },
  { key: "description", header: "Description" },
  { key: "include_context", header: "Includes" },
];

export default function ContextsPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch, isFetching } = useContexts();
  const createMut = useCreateContext();
  const updateMut = useUpdateContext();
  const deleteMut = useDeleteContext();

  const defaultContext = { name: "", description: null as string | null, include_context: null as string | null };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DialplanContext | null>(null);
  const [form, setForm] = useState(defaultContext);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<DialplanContext | null>(null);

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => [c.name, c.description, c.include_context].some((v) => v?.toLowerCase().includes(q)));
  }, [list, query]);

  const openCreate = () => { setEditing(null); setForm(defaultContext); setOpen(true); };
  const openEdit = (c: DialplanContext) => {
    setEditing(c);
    setForm({ name: c.name, description: c.description ?? null, include_context: c.include_context ?? null });
    setOpen(true);
  };
  const openDetail = (c: DialplanContext) => {
    navigate(`/config/contexts/${c.id}`);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, { onSuccess: () => { setOpen(false); toast.success("Context updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(form as typeof form, { onSuccess: () => { setOpen(false); toast.success("Context created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Trunk Contexts"
        description="Click a trunk context to manage its endpoints"
      />

      <Toolbar
        search={{ value: query, onChange: setQuery, placeholder: "Search contexts..." }}
        count={`${filtered.length}${query ? ` of ${list.length}` : ""}`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
        actions={<Button onClick={openCreate} size="sm"><Plus size={14} /> Create Trunk Context</Button>}
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        getRowKey={(c) => c.id}
        onEdit={openEdit}
        onDelete={setPendingDelete}
        emptyMessage={query ? "No matches for your search." : "No contexts configured."}
        onRowClick={openDetail}
        error={error}
        onRetry={() => refetch()}
      />

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit Trunk Context" : "Create Trunk Context"}
        description="A dialplan context groups extensions and routes calls for its endpoints"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </>
        }
      >
        <FormSection title="Context" columns={1}>
          <Field label="Name" required htmlFor="name" info="Unique identifier. Becomes the Asterisk dialplan [context] section name. Used by endpoints to route calls. Example: incoming, outbound-sip.">
            <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="internal" />
          </Field>
          <Field label="Description" htmlFor="description" info="Human-readable note about this context's purpose. Not used by Asterisk. Shown in the UI for reference only.">
            <Textarea id="description" value={form.description ?? ""} onChange={(e) => updateField("description", e.target.value || null)} rows={2} />
          </Field>
          <Field label="Include Context" htmlFor="include_context" info="Inherit all extensions from another context. The referenced context must exist. Example: 'default' includes Asterisk's built-in extensions. Commonly used to chain contexts.">
            <Input id="include_context" value={form.include_context ?? ""} onChange={(e) => updateField("include_context", e.target.value || null)} placeholder="default" />
          </Field>
        </FormSection>
      </Sheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Delete trunk context"
        description={<>Delete <strong>{pendingDelete?.name}</strong>? Endpoints in this context will be affected. This cannot be undone.</>}
        loading={deleteMut.isPending}
        onConfirm={() =>
          pendingDelete &&
          deleteMut.mutate(pendingDelete.id, {
            onSuccess: () => { toast.success("Context deleted"); setPendingDelete(null); },
            onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
          })
        }
      />
    </div>
  );
}
